/**
 * The closed **marker-glyph vocabulary** — the fixed, small set of simple
 * shapes an operator can pick for platform / detection / alert map markers
 * (ADR-033 §9.2; REQ-UI-086 / REQ-UI-087 / REQ-UI-088). Post-inspection
 * GLYPHS batch: the schema was simplified to one glyph-id string per entity
 * type (``map.platform.glyph`` / ``map.detection.glyph`` / ``map.alert.glyph``;
 * the per-NodeType map ``glyph_by_node_type`` was removed, #367 closed), so a
 * single closed vocabulary serves all three types.
 *
 * # One geometry source, two render targets
 *
 * Each glyph is defined **once** as a normalised vertex list in unit space
 * (``[-1, 1]`` on both axes; the visual extent is the unit circle), except
 * ``circle`` which is the analytic special case. That single definition feeds:
 *
 *   - {@link drawGlyphPath} — traces the shape onto a 2D canvas context, used
 *     by {@link registerGlyphIcons} to bake an **SDF** image per glyph that
 *     the MapLibre symbol layers tint via ``icon-color`` (so the increment-6b
 *     by_status / by_alert_type / by_priority / single colour modes keep
 *     working unchanged — only the *shape* is new).
 *   - {@link glyphSvgPathData} — emits the same outline as an SVG ``d``
 *     attribute, used by the editor's glyph picker swatches (crisp at any DPI
 *     in the DOM, trivially testable in jsdom without a real canvas).
 *
 * Defining the geometry once keeps the picker swatch and the painted marker in
 * lock-step (CLAUDE.md §4.4 — no duplicated logic): a swatch is a faithful
 * preview of the glyph the map paints.
 *
 * # Why SDF icons (tintable) rather than coloured sprites
 *
 * A plain (non-SDF) MapLibre image can't be recoloured by ``icon-color`` — its
 * pixels paint as-authored. The marker colour is operator-driven per the 6b
 * colour modes, so the glyph image is registered as a **signed-distance-field**
 * (solid white shape on a transparent field, ``addImage(id, …, {sdf: true})``);
 * MapLibre then tints it per feature with the layer's existing colour
 * expression. One white image per shape serves every colour.
 *
 * # Unknown-id fallback
 *
 * A glyph id the running UI doesn't recognise (a forward-compat Profile, a
 * hand-edited import) resolves to {@link DEFAULT_GLYPH} (``circle``) via
 * {@link normalizeGlyphId} rather than crashing or painting nothing — the same
 * fail-safe discipline as the marker-colour resolvers (CLAUDE.md §8.1; a bad
 * glyph still renders a legible marker).
 *
 * Cross-references:
 *   - REQ-UI-086 (platform glyph) / REQ-UI-087 (detection glyph) /
 *     REQ-UI-088 (alert glyph) / REQ-UI-094 (keyboard-operable, labelled).
 *   - ADR-033 §9.2 / §9 / §9.7 (the ``glyph`` fields).
 *   - map/marker-styling.ts (the colour expressions the ``icon-color`` reuses).
 *   - map/snapshot-layer-impl.ts / detection-layer-impl.ts / alert-layer-impl.ts
 *     (the symbol layers that consume {@link glyphIconId}).
 */
import type { ExpressionSpecification, Map as MapLibreMap, StyleImageMetadata } from "maplibre-gl";

/**
 * The closed glyph vocabulary, in the order the picker lists the swatches. A
 * small set of simple, mutually-distinguishable shapes — operators pattern-
 * match marker shape at a glance (the operator-tooling register, CLAUDE.md
 * §8.1 / the meridian-ui-engineer aesthetic). Keep this short; novelty hurts
 * scannability.
 */
export const GLYPH_IDS = [
  "circle",
  "triangle",
  "square",
  "diamond",
  "hexagon",
  "star",
  "cross",
] as const;

/** A valid glyph id — one of the closed {@link GLYPH_IDS} vocabulary. */
export type GlyphId = (typeof GLYPH_IDS)[number];

/**
 * The fallback glyph (``circle``) for an unrecognised id (forward-compat
 * Profile / hand-edited import). The schema's per-type defaults are
 * ``platform="triangle"`` / ``detection="circle"`` / ``alert="diamond"``; the
 * *fallback* is deliberately the plainest shape so a bad id degrades to a
 * legible neutral marker (CLAUDE.md §8.1).
 */
export const DEFAULT_GLYPH: GlyphId = "circle";

/** Human-facing label per glyph id — the picker swatch's accessible name and
 *  visible tooltip (REQ-UI-094). */
export const GLYPH_LABELS: Readonly<Record<GlyphId, string>> = {
  circle: "Circle",
  triangle: "Triangle",
  square: "Square",
  diamond: "Diamond",
  hexagon: "Hexagon",
  star: "Star",
  cross: "Cross",
};

/** Narrow an arbitrary string to a {@link GlyphId} (closed-vocabulary guard). */
export function isGlyphId(value: string | null | undefined): value is GlyphId {
  return value != null && (GLYPH_IDS as readonly string[]).includes(value);
}

/**
 * Resolve any (possibly unknown / blank / absent) glyph id to a valid
 * {@link GlyphId}, falling back to {@link DEFAULT_GLYPH}. The single
 * unknown-id seam: every consumer (the map layers, the picker) routes the
 * Profile's stored ``glyph`` through this so a forward-compat or hand-edited
 * value never crashes or paints nothing (CLAUDE.md §8.1; REQ-UI-086/087/088).
 */
export function normalizeGlyphId(value: string | null | undefined): GlyphId {
  return isGlyphId(value) ? value : DEFAULT_GLYPH;
}

// ── Geometry: one definition per shape, in unit space ([-1, 1]) ───────────

/**
 * Counter-clockwise outline vertices for the **polygon** glyphs, in unit space
 * — the visual extent is the unit circle (radius 1), so every shape is scaled
 * to the same bounding circle and reads at a consistent size. ``circle`` is the
 * analytic exception (no vertex list; drawn as an arc / SVG ``<path>`` arc).
 *
 * Authored once here; {@link drawGlyphPath} (canvas / SDF) and
 * {@link glyphSvgPathData} (SVG picker) both trace these, so the painted
 * marker and the picker swatch are the same shape (CLAUDE.md §4.4).
 */
const GLYPH_POLYGONS: Readonly<
  Record<Exclude<GlyphId, "circle">, ReadonlyArray<readonly [number, number]>>
> = {
  // Equilateral triangle, apex up. Vertices on the unit circle at
  // -90° / 150° / 30°.
  triangle: regularPolygon(3, -90),
  // Axis-aligned square inscribed in the unit circle (corners at ±45°…).
  square: [
    [-0.707, -0.707],
    [0.707, -0.707],
    [0.707, 0.707],
    [-0.707, 0.707],
  ],
  // Square rotated 45° — a rhombus with vertices on the axes.
  diamond: [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ],
  // Regular hexagon, flat-top (first vertex at 0°… rotated for a pointy side).
  hexagon: regularPolygon(6, -90),
  // Five-pointed star: outer points on the unit circle, inner points at
  // ~0.5 radius, alternating (10 vertices), first outer point up.
  star: starPolygon(5, 1, 0.5, -90),
  // Plus / cross — a 12-vertex axis-aligned plus with arm half-width 0.36.
  cross: crossPolygon(0.36),
};

/**
 * Vertices of a regular ``n``-gon on the unit circle, starting at
 * ``startDeg`` and going counter-clockwise. Used for triangle / hexagon so the
 * shapes are exactly regular rather than hand-typed.
 */
function regularPolygon(n: number, startDeg: number): ReadonlyArray<readonly [number, number]> {
  const start = (startDeg * Math.PI) / 180;
  const pts: Array<readonly [number, number]> = [];
  for (let i = 0; i < n; i += 1) {
    const a = start + (i * 2 * Math.PI) / n;
    pts.push([round(Math.cos(a)), round(Math.sin(a))]);
  }
  return pts;
}

/**
 * Vertices of an ``n``-pointed star alternating ``outer`` / ``inner`` radius,
 * starting at ``startDeg`` (an outer point) and going counter-clockwise.
 */
function starPolygon(
  n: number,
  outer: number,
  inner: number,
  startDeg: number,
): ReadonlyArray<readonly [number, number]> {
  const start = (startDeg * Math.PI) / 180;
  const pts: Array<readonly [number, number]> = [];
  for (let i = 0; i < n * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = start + (i * Math.PI) / n;
    pts.push([round(r * Math.cos(a)), round(r * Math.sin(a))]);
  }
  return pts;
}

/**
 * Vertices of an axis-aligned plus / cross with arm half-width ``w`` (the arms
 * extend to ±1 on each axis). 12 vertices, counter-clockwise from the top-left
 * inner corner.
 */
function crossPolygon(w: number): ReadonlyArray<readonly [number, number]> {
  return [
    [-w, -1],
    [w, -1],
    [w, -w],
    [1, -w],
    [1, w],
    [w, w],
    [w, 1],
    [-w, 1],
    [-w, w],
    [-1, w],
    [-1, -w],
    [-w, -w],
  ];
}

/** Round to 3 dp so the authored vertex tables + SVG output stay tidy. */
function round(x: number): number {
  return Math.round(x * 1000) / 1000;
}

// ── Canvas / SDF rendering (map markers) ──────────────────────────────────

/**
 * Trace the glyph's outline onto a 2D canvas context, centred at
 * (``cx``, ``cy``) with visual radius ``r`` (px). Does **not** fill / stroke —
 * the caller owns paint — so the same path can be filled white for the SDF
 * bake. ``circle`` traces an arc; every other glyph traces its
 * {@link GLYPH_POLYGONS} outline scaled by ``r``.
 *
 * Exported for unit testability — a recording fake context can assert the path
 * commands (``arc`` for circle; ``moveTo`` + N×``lineTo`` + ``closePath`` for a
 * polygon) without a real canvas / pixel readback.
 *
 * @param ctx - The 2D context (real canvas at runtime; a recording fake in
 *     tests).
 * @param glyph - The glyph id (already normalised by the caller).
 * @param cx - Centre x (px).
 * @param cy - Centre y (px).
 * @param r - Visual radius (px) — the unit-space extent maps to this.
 *
 * Cross-references:
 *   - REQ-UI-086/087/088 (the marker shape); ADR-033 §9.
 */
export function drawGlyphPath(
  ctx: CanvasPath,
  glyph: GlyphId,
  cx: number,
  cy: number,
  r: number,
): void {
  if (glyph === "circle") {
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    return;
  }
  const pts = GLYPH_POLYGONS[glyph];
  pts.forEach(([ux, uy], i) => {
    const x = cx + ux * r;
    const y = cy + uy * r;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  ctx.closePath();
}

/**
 * The MapLibre image id under which {@link registerGlyphIcons} registers a
 * glyph's SDF — namespaced so it never collides with the BFF text-glyph atlas
 * or any future sprite. The symbol layers reference this from ``icon-image``.
 */
export function glyphIconId(glyph: GlyphId): string {
  return `meridian-glyph-${glyph}`;
}

/**
 * MapLibre ``icon-image`` value expression: read the feature's ``glyph``
 * property and map it onto the registered SDF id, defaulting to the
 * {@link DEFAULT_GLYPH} circle for an absent/unknown value. A constant string
 * is also a valid ``icon-image`` (when a layer paints a single fixed glyph);
 * this helper is used where the glyph is uniform per layer (the active
 * Profile's single ``map.<type>.glyph``), so it returns the constant id —
 * keeping the symbol layout simple and the SDF lookup a single id.
 */
export function glyphIconImage(glyph: GlyphId): string {
  return glyphIconId(glyph);
}

/** The pixel size of the baked SDF image (a power-of-two-ish square). Larger
 *  than any on-screen marker radius so the SDF downsamples cleanly; the symbol
 *  layer's ``icon-size`` scales it to the marker radius at paint time. */
export const GLYPH_SDF_SIZE_PX = 48;

/**
 * Bake one SDF image per glyph and register them on the live map via
 * ``addImage(id, …, { sdf: true })`` so the symbol layers can tint them with
 * ``icon-color``. Idempotent — skips any glyph whose image is already
 * registered (``hasImage``), so it's safe to call on every ``styledata`` /
 * re-attach.
 *
 * Each image is a solid **white** shape on a transparent field; ``sdf: true``
 * tells MapLibre to treat the alpha as a recolourable mask, so the layer's
 * ``icon-color`` (the existing by_status / by_alert_type / by_priority / single
 * expression) paints the operator's chosen colour. The white-on-transparent
 * bake is what keeps the 6b colour modes working after the shape change.
 *
 * Canvas acquisition is defensive: if a 2D context isn't available (a headless
 * / jsdom path without a canvas backend), the glyph is skipped with a loud log
 * rather than throwing (CLAUDE.md §8.1 — operator-visibility; a missing icon
 * surfaces in DevTools, it doesn't crash the map). In tests a recording fake
 * ``addImage`` + a stub ``getContext`` exercise the registration without real
 * pixels.
 *
 * @param map - The live MapLibre map (post-``load``).
 * @returns The glyph ids that were registered on this call (excludes ones
 *     already present) — for test assertions + diagnostics.
 *
 * Cross-references:
 *   - REQ-UI-086/087/088 (marker glyphs); ADR-033 §9.
 *   - map/snapshot-layer-impl.ts etc. (the symbol layers that consume these).
 */
export function registerGlyphIcons(map: MapLibreMap): GlyphId[] {
  const registered: GlyphId[] = [];
  // ``sdf: true`` makes the alpha a recolourable mask (so ``icon-color`` tints
  // it); ``pixelRatio: 1`` — the SDF is authored at device-independent px and
  // ``icon-size`` scales it (the bake size is already generous, so no @2x).
  const sdfMeta: StyleImageMetadata = { sdf: true, pixelRatio: 1 };
  for (const glyph of GLYPH_IDS) {
    const id = glyphIconId(glyph);
    if (map.hasImage(id)) {
      continue;
    }
    const image = bakeGlyphSdf(glyph);
    if (image === null) {
      // No 2D canvas backend at all (jsdom / a headless path) — the first bake
      // failing means every bake will. Warn ONCE and bail (the symbol layers
      // then reference an absent icon, which MapLibre logs but tolerates; the
      // rest of the map still renders). CLAUDE.md §8.1 — visible, not silent,
      // and not crashing.
      console.warn(
        "[meridian/map/glyphs] no 2D canvas context; skipped marker-glyph SDF registration",
      );
      return registered;
    }
    map.addImage(id, image, sdfMeta);
    registered.push(glyph);
  }
  return registered;
}

/**
 * Bake a single glyph into an ``ImageData`` (solid white shape, transparent
 * field) sized {@link GLYPH_SDF_SIZE_PX}. Returns ``null`` when no 2D context
 * is obtainable (headless backend) so {@link registerGlyphIcons} can skip-and-
 * log rather than throw.
 *
 * Exported for unit testability — a test can stub the canvas factory to assert
 * the draw path + the white fill without a real raster backend.
 */
export function bakeGlyphSdf(glyph: GlyphId): ImageData | null {
  const size = GLYPH_SDF_SIZE_PX;
  const canvas = createGlyphCanvas(size);
  if (canvas === null) {
    return null;
  }
  // ``OffscreenCanvas`` and ``HTMLCanvasElement`` both return a 2D context with
  // the same surface for the calls below, but their ``getContext`` overloads
  // type to different unions; the explicit 2D-context type unifies them. jsdom
  // *throws* "Not implemented" rather than returning ``null`` when no canvas
  // backend is installed, so guard the whole acquisition + draw in a try/catch:
  // a ``null`` return lets :func:`registerGlyphIcons` skip-and-log instead of
  // crashing the map (CLAUDE.md §8.1).
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  try {
    ctx = canvas.getContext("2d") as
      CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  } catch {
    return null;
  }
  if (ctx === null) {
    return null;
  }
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  // Inset the visual radius slightly so the shape's edge isn't clipped by the
  // image border (the SDF needs a transparent margin around the mask).
  const r = size / 2 - 2;
  drawGlyphPath(ctx, glyph, size / 2, size / 2, r);
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

/**
 * Create a square canvas of side ``size`` for the SDF bake. Prefers
 * ``OffscreenCanvas`` (no DOM attachment) and falls back to a detached
 * ``<canvas>`` element. Returns ``null`` when neither is available (a pure
 * Node path with no canvas backend) so callers degrade gracefully.
 */
function createGlyphCanvas(size: number): OffscreenCanvas | HTMLCanvasElement | null {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(size, size);
  }
  if (typeof document !== "undefined") {
    const el = document.createElement("canvas");
    el.width = size;
    el.height = size;
    return el;
  }
  return null;
}

/**
 * The baked SDF's visual radius in source px — the inset radius
 * {@link bakeGlyphSdf} draws (``GLYPH_SDF_SIZE_PX / 2 - 2``). Used to convert a
 * desired on-screen marker radius into a symbol-layer ``icon-size`` multiplier
 * (``icon-size = targetRadiusPx / GLYPH_SDF_RADIUS_PX``).
 */
export const GLYPH_SDF_RADIUS_PX = GLYPH_SDF_SIZE_PX / 2 - 2;

/**
 * Convert ``[zoom, radiusPx]`` stops (the same stops the circle layers used for
 * ``circle-radius``) into a symbol-layer ``icon-size`` ``interpolate``
 * expression, so a glyph SDF renders at the *same* on-screen size the circle
 * marker did. ``icon-size`` scales the source image, so each stop's multiplier
 * is ``radiusPx / GLYPH_SDF_RADIUS_PX``. Keeping the shape change size-neutral
 * means the platform / detection / alert markers stay at their established
 * scale (no regression to the 6a/6b visual size).
 *
 * @param stops - ``[zoom, radiusPx]`` pairs (the existing marker-radius stops).
 * @returns A MapLibre ``["interpolate", ["linear"], ["zoom"], …]`` expression
 *     for the symbol layer's ``icon-size``.
 *
 * Cross-references:
 *   - REQ-UI-086/087/088 (marker glyphs at the established scale).
 */
export function iconSizeExpression(
  stops: ReadonlyArray<readonly [number, number]>,
): ExpressionSpecification {
  const flat = stops.flatMap(([z, r]) => [z, r / GLYPH_SDF_RADIUS_PX]);
  return ["interpolate", ["linear"], ["zoom"], ...flat];
}

// ── SVG rendering (editor picker swatches) ────────────────────────────────

/** The viewBox the SVG picker swatches use — centred on the origin so the
 *  unit-space (``[-1, 1]``) vertices map directly (a vertex at ``1`` lands at
 *  the viewBox edge). */
export const GLYPH_SVG_VIEWBOX = "-1.15 -1.15 2.3 2.3";

/**
 * Emit the glyph outline as an SVG ``<path d="…">`` value in unit space
 * (``[-1, 1]``), matching {@link drawGlyphPath}'s geometry exactly so a picker
 * swatch is a faithful preview of the painted marker. ``circle`` emits a
 * two-arc circle path (so the same ``<path>`` element renders every glyph — the
 * picker doesn't branch on element type).
 *
 * Exported for unit testability + so the {@link GlyphIcon} component (in
 * ``glyph-icon.tsx``) and any future legend share one path source.
 *
 * @param glyph - The glyph id (already normalised by the caller).
 * @returns The SVG path ``d`` attribute string.
 */
export function glyphSvgPathData(glyph: GlyphId): string {
  if (glyph === "circle") {
    // Unit circle as two arcs (radius 1, centre origin). ``A rx ry rot
    // large-arc sweep x y`` — two half-circles close a full circle.
    return "M -1 0 A 1 1 0 0 1 1 0 A 1 1 0 0 1 -1 0 Z";
  }
  const pts = GLYPH_POLYGONS[glyph];
  const [first, ...rest] = pts;
  const head = `M ${first[0]} ${first[1]}`;
  const tail = rest.map(([x, y]) => `L ${x} ${y}`).join(" ");
  return `${head} ${tail} Z`;
}
